import { LOG_LEVELS, CURRENT_LOG_LEVEL, DEV_MODE_EXCEPTIONS } from './constants';

/**
 * RASP Logger Utility
 * 
 * Provides controlled logging for RASP system with different log levels
 * and development mode exceptions to reduce noise.
 */
class RaspLogger {
    constructor() {
        this.logLevel = CURRENT_LOG_LEVEL;
        this.isDev = __DEV__;
    }

    /**
     * Log an error message
     */
    error(message, ...args) {
        if (this.logLevel >= LOG_LEVELS.ERROR) {
            console.error(`RASP - ${message}`, ...args);
        }
    }

    /**
     * Log a warning message
     */
    warn(message, ...args) {
        if (this.logLevel >= LOG_LEVELS.WARN) {
            console.warn(`RASP - ${message}`, ...args);
        }
    }

    /**
     * Log an info message
     */
    info(message, ...args) {
        if (this.logLevel >= LOG_LEVELS.INFO) {
            console.log(`RASP - ${message}`, ...args);
        }
    }

    /**
     * Log a debug message
     */
    debug(message, ...args) {
        if (this.logLevel >= LOG_LEVELS.DEBUG) {
            console.log(`RASP - ${message}`, ...args);
        }
    }

    /**
     * Log a verbose message (initialization details, etc.)
     */
    verbose(message, ...args) {
        if (this.logLevel >= LOG_LEVELS.VERBOSE) {
            console.log(`RASP - ${message}`, ...args);
        }
    }

    /**
     * Log initialization message (reduced in dev mode)
     */
    init(message, ...args) {
        if (this.isDev && DEV_MODE_EXCEPTIONS.REDUCE_INITIALIZATION_LOGS) {
            // Only log important initialization messages in dev
            if (message.includes('initialized successfully') || message.includes('failed')) {
                this.debug(message, ...args);
            }
        } else {
            this.info(message, ...args);
        }
    }

    /**
     * Log security event with dev mode filtering
     */
    securityEvent(eventType, eventData) {
        // Filter out expected dev mode events
        if (this.isDev) {
            if (eventType === 'emulator_detected' && DEV_MODE_EXCEPTIONS.IGNORE_EMULATOR_DETECTION) {
                this.debug(`Security Event [${eventType}] (ignored in dev):`, eventData);
                return;
            }
            
            if (eventType === 'suspicious_environment' && DEV_MODE_EXCEPTIONS.IGNORE_DEBUGGING_TOOLS) {
                const indicators = eventData.details?.indicators || [];
                const modifications = eventData.details?.modifications || [];
                
                // Filter out dev-related indicators
                const filteredIndicators = indicators.filter(indicator => 
                    indicator !== 'debugging_globals' && 
                    indicator !== 'devtools_open' && 
                    indicator !== 'react_devtools'
                );
                
                const filteredModifications = modifications.filter(mod => 
                    mod !== 'console_modified' && 
                    mod !== 'settimeout_modified'
                );
                
                // Only log if there are non-dev indicators/modifications
                if (filteredIndicators.length > 0 || filteredModifications.length > 0) {
                    this.warn(`Security Event [${eventType}]:`, {
                        ...eventData,
                        details: {
                            ...eventData.details,
                            indicators: filteredIndicators,
                            modifications: filteredModifications
                        }
                    });
                } else {
                    this.debug(`Security Event [${eventType}] (dev tools ignored):`, eventData);
                }
                return;
            }
        }
        
        // Log security events based on severity
        const severity = eventData.severity || 'medium';
        switch (severity) {
            case 'critical':
            case 'high':
                this.error(`Security Event [${eventType}]:`, eventData);
                break;
            case 'medium':
                this.warn(`Security Event [${eventType}]:`, eventData);
                break;
            case 'low':
                this.info(`Security Event [${eventType}]:`, eventData);
                break;
            case 'none':
                this.debug(`Security Event [${eventType}]:`, eventData);
                break;
            default:
                this.info(`Security Event [${eventType}]:`, eventData);
        }
    }

    /**
     * Log threat level changes
     */
    threatLevel(level, previousLevel) {
        if (this.isDev && (level === 'low' || level === 'none')) {
            // Don't spam threat level changes for low/none in dev
            this.debug(`Threat level updated: ${previousLevel} → ${level}`);
        } else {
            this.info(`Threat level updated: ${previousLevel} → ${level}`);
        }
    }

    /**
     * Log detector status
     */
    detector(detectorName, status, details) {
        switch (status) {
            case 'initializing':
                this.verbose(`Initializing ${detectorName} Detector`);
                break;
            case 'initialized':
                this.init(`${detectorName} detector initialized`);
                break;
            case 'failed':
                this.error(`${detectorName} detector failed to initialize:`, details);
                break;
            case 'cleanup':
                this.verbose(`${detectorName} detector cleanup completed`);
                break;
            default:
                this.debug(`${detectorName} detector: ${status}`, details);
        }
    }

    /**
     * Log service status
     */
    service(status, details) {
        switch (status) {
            case 'initializing':
                this.info('Initializing security service');
                break;
            case 'initialized':
                this.info('Security service initialized successfully');
                break;
            case 'failed':
                this.error('Security service initialization failed:', details);
                break;
            case 'cleanup':
                this.info('Security service cleanup completed');
                break;
            default:
                this.debug(`Security service: ${status}`, details);
        }
    }

    /**
     * Set log level dynamically
     */
    setLogLevel(level) {
        this.logLevel = level;
        this.info(`Log level changed to: ${Object.keys(LOG_LEVELS)[level]}`);
    }

    /**
     * Get current log level
     */
    getLogLevel() {
        return this.logLevel;
    }
}

// Create singleton instance
const raspLogger = new RaspLogger();

export default raspLogger;
